import type * as api from '@gitbook/api-client';
import {
    createPalette,
    PaletteButton,
    type PaletteCommandDef,
    type PaletteGroupDef,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import type { ButtonProps } from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { useNavigation } from '../../../navigation';
import { apiClient, type SiteCapability, type FeatureUpgradeAction } from '../../../state';
import { useSiteAudienceCommand } from '../../AdaptiveContent';
import { getPaletteLocationOptions } from './getPaletteLocationOptions';
import { getRestrictedSiteItemPaletteGroups } from './getRestrictedSiteItemPaletteGroups';
import { useDeleteSiteItems } from './useDeleteSiteItems';
import { useMoveSiteExternalLink } from './useMoveSiteExternalLink';
import { useSiteContentDraftCommand } from './useSiteContentDraft';

export interface SiteExternalLinkActionsButtonProps extends Pick<ButtonProps, 'size' | 'disabled'> {
    siteExternalLink: api.SiteExternalLink;
    site: api.Site;
    organization: api.Organization;
    sitePermissions: SiteCapability;
    siteStructure: api.SiteStructure;
    upgrade: FeatureUpgradeAction | undefined;
}

export function SiteExternalLinkActionsButton(props: SiteExternalLinkActionsButtonProps) {
    const {
        siteExternalLink,
        site,
        organization,
        sitePermissions,
        siteStructure,
        upgrade,
        ...buttonProps
    } = props;
    const { navigate } = useNavigation();
    const canAuthor = siteStructure.type === 'sections' && sitePermissions.canAdminSite;
    const deleteSiteItems = useDeleteSiteItems({ organization, site });
    const moveSiteExternalLink = useMoveSiteExternalLink({ organization, site, siteStructure });
    const draftCommand = useSiteContentDraftCommand({
        organization,
        site,
        siteStructure,
        content: siteExternalLink,
        canCreateSite: sitePermissions.canCreateSite,
    });
    const siteAudience = useSiteAudienceCommand({
        organizationId: organization.id,
        site,
        target: 'external-link',
        condition: siteExternalLink.condition,
        readOnly: !canAuthor,
        onChange: async (condition) => {
            await apiClient.siteExternalLinks.updateSiteExternalLinkById(
                organization.id,
                site.id,
                siteExternalLink.id,
                { condition: condition ?? null },
                { returnPreference: 'minimal' }
            );
        },
    });
    const actionsGroup = getActionsGroup({
        canAuthor,
        hasCondition: Boolean(siteExternalLink.condition),
        authorCommands: [
            {
                key: 'siteExternalLink.edit',
                title: 'Edit navigation item',
                icon: Icon.Edit,
                trigger: () => {
                    navigate({
                        screen: {
                            type: 'site',
                            organization: organization.id,
                            site: site.id,
                            tab: {
                                type: 'structure',
                                selected: siteExternalLink.id,
                            },
                        },
                    });
                },
            },
            draftCommand,
            siteAudience.command,
            {
                key: 'siteExternalLink.move',
                title: 'Move to…',
                icon: Icon.ArrowRight,
                descendant: () => moveSiteExternalLink(siteExternalLink),
            },
            {
                key: 'siteExternalLink.delete',
                title: 'Remove',
                icon: Icon.Trashcan,
                kind: 'danger' as const,
                trigger: () => deleteSiteItems([siteExternalLink]),
            },
        ],
        conditionCommand: siteAudience.command,
    });

    return (
        <>
            {siteAudience.dialogs}
            <PaletteButton
                {...buttonProps}
                kind="blank"
                label="Actions"
                leadingIcon={Icon.MoreVertical}
                iconOnly
                palette={() =>
                    createPalette({
                        groups: [
                            {
                                key: 'open',
                                commands: [
                                    ...getPaletteLocationOptions(siteExternalLink.url, {
                                        openTitle: 'Open link',
                                    }).commands,
                                ],
                            },
                            ...(canAuthor && upgrade
                                ? getRestrictedSiteItemPaletteGroups({
                                      item: siteExternalLink,
                                      upgrade,
                                      deleteSiteItems,
                                      sitePermissions,
                                  })
                                : [actionsGroup]),
                        ].filter(filterOutNullable),
                    })
                }
            />
        </>
    );
}

function getActionsGroup(args: {
    canAuthor: boolean;
    hasCondition: boolean;
    authorCommands: Array<PaletteCommandDef | null | undefined>;
    conditionCommand: PaletteCommandDef | null | undefined;
}): PaletteGroupDef | null {
    const { canAuthor, hasCondition, authorCommands, conditionCommand } = args;
    if (canAuthor) {
        return {
            key: 'edit',
            commands: authorCommands.filter(filterOutNullable),
        };
    }
    if (!hasCondition || !conditionCommand) {
        return null;
    }
    return {
        key: 'condition',
        commands: [conditionCommand],
    };
}
